'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import ImagePlaceholder from '@/components/ui/ImagePlaceholder';

// ---------------------------------------------------------------------------
// VideoEmbed  - lightweight, privacy-friendly player for YouTube or a file
// ---------------------------------------------------------------------------
// Shows a poster + play button and only loads the player on click: for YouTube
// that means no third-party scripts or cookies until the visitor opts in, and
// for a hosted file it means preload="none", so a 9MB MP4 costs nothing until
// somebody asks for it.
//
// Both sources are supported because video slots are admin-managed and a slot
// can be switched from a self-hosted file to YouTube without a code change
// (see lib/videos/store.ts). `src` wins when both are supplied.
//
// With neither, it renders a clearly-marked placeholder so the layout is ready
// for a real video to be dropped in.
//
// Usage: <VideoEmbed videoId="dQw4w9WgXcQ" … /> or <VideoEmbed src="/assets/video/x.mp4" … />
// ---------------------------------------------------------------------------

interface VideoEmbedProps {
  /** YouTube video id (the part after `v=`). Leave empty for a placeholder. */
  videoId?: string;
  /** Hosted file URL. Takes precedence over videoId when both are set. */
  src?: string;
  /**
   * Frame shape. 'portrait' is capped in width rather than height: a 9:16 clip
   * given a full-width column would be over 1000px tall beside two paragraphs.
   */
  aspect?: 'video' | 'portrait' | 'square';
  title?: string;
  /** Local poster image; falls back to a branded placeholder if omitted. */
  poster?: string;
  className?: string;
  /**
   * Fill the parent instead of holding a 16:9 box. For layouts that size the
   * media themselves  - the manufacturing card gives it a half-column to fill,
   * and an intrinsic aspect ratio would leave a gap beside the taller text
   * column. The parent is then responsible for the rounding and clipping.
   */
  fill?: boolean;
}

const ASPECT = {
  video: 'aspect-video',
  // The cap is for the single-column layouts below lg, where the frame would
  // otherwise take the full text width and stand over 1000px tall. Where a
  // section splits its columns by orientation (VIDEO_COLUMNS) the column is
  // already the narrower one, and this sits just above it so the frame fills it.
  portrait: 'aspect-9/16 mx-auto max-w-sm',
  square: 'aspect-square mx-auto max-w-[26rem]',
} as const;

export default function VideoEmbed({
  videoId,
  src,
  title = 'Watch the walkthrough',
  poster,
  className = '',
  aspect = 'video',
  fill = false,
}: VideoEmbedProps) {
  const [playing, setPlaying] = useState(false);
  const hasFile = Boolean(src);
  const hasVideo = hasFile || Boolean(videoId);

  return (
    <div
      className={`group relative overflow-hidden bg-foreground/90 ${
        fill ? 'h-full w-full' : `${ASPECT[aspect]} w-full rounded-2xl shadow-cinematic`
      } ${className}`}
    >
      {playing && hasFile ? (
        // No <track>: there are no captions for this file yet. Worth adding a
        // WebVTT track per video once they exist.
        <video
          className="absolute inset-0 h-full w-full bg-foreground object-cover"
          src={src}
          poster={poster}
          controls
          autoPlay
          playsInline
          preload="none"
        >
          <a href={src}>Download the video</a>
        </video>
      ) : playing && hasVideo ? (
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
          title={title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          {/* Poster */}
          {poster ? (
            <AppImage
              src={poster}
              alt={title}
              fill
              className="h-full w-full object-cover opacity-75 transition-opacity duration-300 group-hover:opacity-60"
            />
          ) : (
            <ImagePlaceholder label="Video placeholder" />
          )}

          <div className="absolute inset-0 bg-linear-to-t from-foreground/60 via-foreground/20 to-transparent" />

          {/* Play / coming-soon control */}
          <button
            type="button"
            onClick={() => hasVideo && setPlaying(true)}
            disabled={!hasVideo}
            aria-label={hasVideo ? `Play video: ${title}` : 'Video coming soon'}
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-foreground disabled:cursor-default"
          >
            <span
              className={`flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-dramatic transition-transform duration-300 ${
                hasVideo ? 'group-hover:scale-110' : 'opacity-90'
              }`}
            >
              {/* Inline play triangle */}
              <svg
                viewBox="0 0 24 24"
                width="26"
                height="26"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M8 5.5v13a1 1 0 0 0 1.52.85l10.5-6.5a1 1 0 0 0 0-1.7L9.52 4.65A1 1 0 0 0 8 5.5z" />
              </svg>
            </span>
            <span className="rounded-full bg-foreground/60 px-4 py-1.5 font-cta text-sm font-medium text-primary-foreground backdrop-blur-sm">
              {/* The slot's own title, not a fixed string: these slots are
                  admin-managed, so a hard-coded label misdescribes any slot
                  that is not the manufacturing film. */}
              {hasVideo ? title : `${title}  - coming soon`}
            </span>
          </button>
        </>
      )}
    </div>
  );
}
